import { useEffect, useRef, useState } from "react";
import { Linkedin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import LinkedInGuide from "./LinkedInGuide";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function LinkedInImport({ onImported, compact = false, testid = "linkedin-import" }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose the PDF you saved from LinkedIn.");
    if (file.size > 8 * 1024 * 1024) return toast.error("That file is over 8MB. Please choose the LinkedIn PDF.");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/cv/from-linkedin", form, { headers: { "Content-Type": "multipart/form-data" } });
      const started = Date.now();
      while (alive.current && Date.now() - started < 180000) {
        await sleep(2500);
        const { data: job } = await api.get(`/cv/import/${data.id}`);
        if (job.status === "done") {
          toast.success("Your CV is ready, built from your LinkedIn profile.");
          await onImported?.();
          return;
        }
        if (job.status === "failed") throw new Error("failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't build your CV this time. Please try again.");
    } finally {
      if (alive.current) setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      {!compact && <LinkedInGuide />}
      <div data-testid={`${testid}-dropzone`} role="button" tabIndex={0} aria-label="Upload your LinkedIn PDF"
        onClick={() => !busy && ref.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); !busy && upload(e.dataTransfer.files?.[0]); }}
        className={`cursor-pointer rounded-2xl border-2 border-dashed text-center transition-colors ${compact ? "p-6" : "p-8"} ${over ? "border-brand bg-brand-soft" : "border-line-strong/50 bg-surface hover:border-brand/60 hover:bg-brand-soft/40"}`}>
        <input ref={ref} data-testid={`${testid}-file-input`} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="w-12 h-12 mx-auto rounded-xl bg-brand-soft flex items-center justify-center mb-3">
          {busy ? <Loader2 className="w-6 h-6 text-brand animate-spin" /> : <Linkedin className="w-6 h-6 text-brand" />}
        </div>
        <p className="font-semibold text-ink">{busy ? "Writing your CV from your profile…" : "Drop your LinkedIn PDF here or tap to choose"}</p>
        <p className="text-sm text-faint mt-1">{busy ? "About 30 to 60 seconds. Please keep this page open." : "The PDF from LinkedIn's Save to PDF · up to 8MB"}</p>
      </div>
      {compact && <LinkedInGuide compact uploadWhere="above" />}
    </div>
  );
}
